import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Card from "#/components/shared/card";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import useArticleReviewsList from "#/admin/article-reviews/hooks/useArticleReviewsList";
import { ArticleReview } from "#/services/articleReviews";
import { ArticleReviewList } from "#/admin/article-reviews/components/ArticleReviewList";

function sortReviews(items: ArticleReview[]) {
  return [...items].sort((a, b) => {
    const aDate =
      new Date(
        (a as { created_at?: string }).created_at ||
          (a as { updated_at?: string }).updated_at ||
          (a as { createdAt?: string }).createdAt ||
          (a as { updatedAt?: string }).updatedAt ||
          0
      ).getTime();
    const bDate =
      new Date(
        (b as { created_at?: string }).created_at ||
          (b as { updated_at?: string }).updated_at ||
          (b as { createdAt?: string }).createdAt ||
          (b as { updatedAt?: string }).updatedAt ||
          0
      ).getTime();
    return bDate - aDate;
  });
}

type ArticleReviewsSummaryProps = {
  articleId?: string | null;
};

export function ArticleReviewsSummary({ articleId }: ArticleReviewsSummaryProps) {
  const navigate = useNavigate();
  const listQuery = useArticleReviewsList(
    { articleId: articleId ?? undefined },
    { enabled: Boolean(articleId) }
  );

  const reviews = useMemo(() => {
    const items = Array.isArray(listQuery.data) ? listQuery.data : [];
    return sortReviews(items);
  }, [listQuery.data]);

  return (
    <Card className="w-full" variant="default" elevation={1}>
      <Card.Header divider>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Typography variant="h4" className="text-brown-900">
              Article reviews
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              {listQuery.isLoading
                ? "Loading review history…"
                : `${reviews.length} review${reviews.length === 1 ? "" : "s"} found.`}
            </Typography>
          </div>
          <ActionButton
            tone="secondary"
            onClick={() =>
              navigate(
                `/admin/content?tab=reviews&articleId=${encodeURIComponent(
                  articleId || ""
                )}`
              )
            }
          >
            View all reviews
          </ActionButton>
        </div>
      </Card.Header>
      <Card.Body className="bg-white">
        {listQuery.isLoading ? (
          <Typography variant="body2" className="text-brown-600">
            Loading reviews…
          </Typography>
        ) : listQuery.isError ? (
          <Typography variant="body2" className="text-terracotta">
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : "Unable to load reviews."}
          </Typography>
        ) : reviews.length === 0 ? (
          <Typography variant="body2" className="text-brown-600">
            No reviews yet for this article.
          </Typography>
        ) : (
          <ArticleReviewList
            items={reviews}
            showArticleId={false}
            showRevisionId
          />
        )}
      </Card.Body>
    </Card>
  );
}

export default ArticleReviewsSummary;
